/* eslint-disable @typescript-eslint/no-unsafe-argument */
"use client";

import styled, { css, CSSProp, DefaultTheme } from "styled-components";
import _merge from "lodash/merge";
import { TinyColor, mostReadable } from "@ctrl/tinycolor";
import {
  color,
  borderRadius,
  border,
  font,
  spacing,
  Devices,
  boxShadow,
  bp,
  zIndex,
  fontFamily,
  MediaQueries,
} from "../constants/styles";
import { AnyObject, SpaceTheme, SpaceThemeNames } from "../models";
import type {
  ElementBorderProps,
  ElementFlexProps,
  ElementPositioningProps,
  ElementTransitionProps,
  SpacingValue,
  VariableComponent,
} from "./ComponentProps";

type Device = number;

// MARK : Themes
// -------------------------------------------------------------------------------
export function getThemeName(props: { theme?: Partial<SpaceTheme> }): SpaceThemeNames {
  return props?.theme?.themeName || SpaceThemeNames.classic;
}

// MARK : Colors
/*-------------------------------------------------------------------------------*/

export function getColor(propValue: string, fallback: string = null): string {
  if (propValue) {
    if (color[propValue]) {
      return color[propValue];
    }
    return propValue;
  }
  return fallback ? getColor(fallback) : null;
}

const themeColorNameMap = {
  primary: "primaryColor",
  primaryDark: "primaryColorDark",
  primaryLight: "primaryColorLight",
  primaryTint: "primaryColorTint",
  noteSuccess: "noteSuccessColor",
  noteInfo: "noteInfoColor",
  noteWarning: "noteWarningColor",
  noteNeutral: "noteNeutralColor",
  assetLight: "assetLightColor",
  assetDark: "assetDarkColor",
};

export function getThemeColor(
  propValue: string,
  theme: Record<string, string>,
  def: string
): string {
  const themeName: string = themeColorNameMap[propValue] || propValue;

  if (theme?.active && theme?.[themeName]) {
    return theme[themeName];
  }
  const themeDef: string = themeColorNameMap[def] || propValue;
  if (theme?.active && theme?.[themeDef]) {
    return theme[themeDef];
  }
  return getColor(propValue.replace("Color", ""), def);
}

export type ThemedProps = { theme?: DefaultTheme };
export function getPrimaryColor(props: ThemedProps): string {
  if (props.theme.primaryColor) return props.theme.primaryColor;
  return color.primary;
}

export function getPrimaryColorTint(props: ThemedProps): string {
  if (props.theme.primaryColorTint) return props.theme.primaryColorTint;
  return "#fff";
}

function replaceColors(str: string): string {
  if (!str) return str;
  return str
    .split(" ")
    .map(comp => {
      return color[comp] || comp;
    })
    .join(" ");
}

export function getShadow(propValue: string, def?: string): string {
  return (
    (propValue && (boxShadow[propValue] || replaceColors(propValue))) ||
    (def ? getShadow(def) : null)
  );
}

export function getColorBasedOnLightness(
  hex: string,
  lightReturn: string,
  darkReturn: string
): string {
  const lightColor = getColor(lightReturn);
  const darkColor = getColor(darkReturn);
  return mostReadable(new TinyColor(hex), [lightColor, darkColor]).toHexString();
}

export function getTextColor(hex: string) {
  return getColorBasedOnLightness(hex, "black", "white");
}

export function getBlockquoteTextColor(hex: string) {
  return getColorBasedOnLightness(hex, "#58595a", "#ffffff");
}

export function getIconFill(hex: string) {
  return getColorBasedOnLightness(hex, "black", "white");
}

export function getCodeSnippetTextColor(hex: string) {
  return getColorBasedOnLightness(hex, "rgba(0,0,0,0.6)", "rgba(255,255,255,0.6)");
}

// MARK : Fonts
/*-------------------------------------------------------------------------------*/

type FontObject = {
  fontFamily?: string;
  fontSize?: string | number;
  fontWeight?: string | number;
  lineHeight?: string | number;
  letterSpacing?: string | number;
};

export function getFont(
  propValue: string,
  def: string,
  props?: AnyObject
): FontObject | string | null {
  if (propValue) {
    if (font[propValue]) {
      /**
       * Get the default font object based on the prop value passed.
       * If there is an override or theme font accessible in props, replace font family.
       * NOTE: We may need some font style map if we support multiple fonts in the future.
       */
      const fontObject = { ...font[propValue] };
      const customFontFamily = getCustomFontFamily(props);
      const themeOverrides = getThemeFontOverrides(propValue, def, props);
      if (customFontFamily) fontObject.fontFamily = customFontFamily;
      return _merge(fontObject, themeOverrides);
    }
    if (typeof propValue === "string") {
      console.error(`Invalid font name ${propValue}`);
    }
    return propValue;
  }
  return def ? getFont(def, null, props) : null;
}

function getThemeFontOverrides(propValue: string, def: string, props?: AnyObject) {
  const key = propValue || def;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const themeName = props?.theme?.themeName;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  if (themeName && font.themeOverrides[themeName]?.[key])
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return font.themeOverrides[props.theme.themeName][key];
  return {};
}

export function getCustomFontFamily(props?: {
  as?: string;
  font?: string;
  theme?: Pick<SpaceTheme, "fonts">;
}): string {
  if (props?.as === "code" || props?.font?.includes("code")) return fontFamily.mono.fontFamily;
  if (props?.theme?.fonts?.system?.family) {
    return `"${props.theme.fonts.system.family}", sans-serif`;
  }
  return undefined;
}

// MARK : Borders
/*-------------------------------------------------------------------------------*/

export function getBorderRadius(
  radiusNameOrValue: string | number,
  def: string | number = null,
  props?: { theme?: { themeName?: string } }
): string {
  const themeName = props?.theme?.themeName,
    key = radiusNameOrValue,
    overrides = borderRadius.themeOverrides as Record<string, Record<string, string>>;

  // Theme-aware named radius lookup
  if (themeName && typeof key === "string" && overrides?.[themeName]?.[key]) {
    const val = overrides[themeName][key];
    return (borderRadius[val] as string) || (cssVal(val) as string);
  }

  // Direct token lookup
  if (key && borderRadius[key] && typeof borderRadius[key] === "string") {
    return borderRadius[key] as string;
  }

  // Backward compat: first arg is a theme name
  if (typeof key === "string" && overrides?.[key]?.default) {
    const val = overrides[key].default;
    return (borderRadius[val] as string) || (cssVal(val) as string);
  }

  // Raw CSS value passthrough
  if (key) {
    const raw = cssVal(key);
    if (raw) return raw as string;
  }

  // Fallback to def
  if (def != null) return getBorderRadius(def);

  return null;
}

/**
 *
 * @param propValue the desired border value
 * @param def a default border value if propValue is not defined (optional)
 *
 * Attampts to replace any values in the border definition with a color value
 * This allows for values like `1px solid grayLight` where grayLight will be replaced with
 * the predefined color vlaue.
 */
export function getBorder(propValue: string, def: string = null): string {
  if (propValue) {
    if (border[propValue]) {
      return border[propValue];
    }
    return replaceColors(propValue);
  }
  return def ? getBorder(def) : null;
}

// MARK : Spacing
/*-------------------------------------------------------------------------------*/

function getZIndex(propValue: SpacingValue, def: SpacingValue = null): SpacingValue {
  return (propValue && zIndex[propValue]) || propValue || (def ? getZIndex(def) : null);
}

export function getResponsiveSpacing(
  device: Device,
  val: SpacingValue,
  fallback: SpacingValue = null
): string | number {
  function getValue(value: string | number) {
    const values = spacing[value];
    if (values) {
      return cssVal(values[device === null ? Devices.desktop : device]);
    }
    return cssVal(value);
  }
  return getValue(val) || getValue(fallback);
}

export function cssVal(val: string | number): string | number {
  if (val === "") return null;
  if (val === null) return null;
  const num = Number(val);
  return Number.isNaN(num) ? val : `${val}px`;
}

export function flexCSS(props: VariableComponent<ElementFlexProps>): CSSProp {
  return css`
    flex: ${props.flex};
    flex-basis: ${props.flexBasis};
    flex-grow: ${props.flexGrow};
    flex-shrink: ${props.flexShrink};
    ${getVariations(props, flexCSS)}
  `;
}

export function positioningCSS(props: VariableComponent<ElementPositioningProps>): CSSProp {
  function getPositionSpacing(device: Device) {
    return css`
      padding: ${getResponsiveSpacing(device, props.p)};
      padding-left: ${getResponsiveSpacing(device, props.pl, props.px)};
      padding-right: ${getResponsiveSpacing(device, props.pr, props.px)};
      padding-bottom: ${getResponsiveSpacing(device, props.pb, props.py)};
      padding-top: ${getResponsiveSpacing(device, props.pt, props.py)};
      margin: ${getResponsiveSpacing(device, props.m)};
      margin-left: ${getResponsiveSpacing(device, props.ml, props.mx)};
      margin-right: ${getResponsiveSpacing(device, props.mr, props.mx)};
      margin-bottom: ${getResponsiveSpacing(device, props.mb, props.my)};
      margin-top: ${getResponsiveSpacing(device, props.mt, props.my)};
      top: ${getResponsiveSpacing(device, props.top)};
      left: ${getResponsiveSpacing(device, props.left)};
      right: ${getResponsiveSpacing(device, props.right)};
      bottom: ${getResponsiveSpacing(device, props.bottom)};
    `;
  }

  return css`
    ${getPositionSpacing(Devices.desktop)}
    width: ${props.width && cssVal(props.width)};
    height: ${props.height && cssVal(props.height)};
    max-width: ${props.maxWidth && cssVal(props.maxWidth)};
    max-height: ${props.maxHeight && cssVal(props.maxHeight)};
    min-width: ${props.minWidth && cssVal(props.minWidth)};
    min-height: ${props.minHeight && cssVal(props.minHeight)};
    text-align: ${props.textAlign};
    justify-self: ${props.justifySelf};
    align-self: ${props.alignSelf};
    z-index: ${getZIndex(props.zIndex)};
    transform: ${props.transform};
    order: ${props.order};
    position: ${props.position};
    display: ${props.display};
    overflow: ${props.overflow};
    overflow-y: ${props.overflowY};
    overflow-x: ${props.overflowX};
    opacity: ${props.opacity};
    ${Devices.mediaQueryForDevice(Devices.tablet)} {
      ${getPositionSpacing(Devices.tablet)}
    }
    ${Devices.mediaQueryForDevice(Devices.mobile)} {
      ${getPositionSpacing(Devices.mobile)}
    }
    ${getVariations(props, positioningCSS)}
  `;
}

export function borderCSS(props: VariableComponent<ElementBorderProps>): CSSProp {
  return css`
    border-radius: ${getBorderRadius(props.borderRadius)};
    border: ${getBorder(props.border)};
    border-top: ${getBorder(props.borderTop)};
    border-left: ${getBorder(props.borderLeft)};
    border-right: ${getBorder(props.borderRight)};
    border-bottom: ${getBorder(props.borderBottom)};
    ${getVariations(props, borderCSS)}
  `;
}

export function transitionCSS(props: VariableComponent<ElementTransitionProps>): CSSProp {
  return css`
    transition: ${props.transition};
    transition-property: ${props.transitionProperty};
    transition-duration: ${props.transitionDuration};
    transition-timing-function: ${props.transitionTimingFunction};
    transition-delay: ${props.transitionDelay};
    ${getVariations(props, transitionCSS)}
  `;
}

export function getVariations<T extends { variations?: T }>(
  props: T,
  render: (props: T) => CSSProp
): string[] {
  const keys = Object.keys(props.variations || {});
  return keys.reduce((res, key) => {
    const val = render(props.variations[key]);
    if (key.startsWith("mq")) {
      const size = key.split(".")[1];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      res.push(mq[size](val));
    } else {
      res.push(css`
        ${key} {
          ${val}
        }
      `);
    }
    return res;
  }, []);
}

export const mq = Object.keys(bp).reduce((acc, label) => {
  acc[label] = style => css`
    @media screen and (max-width: ${bp[label]}px) {
      ${style};
    }
  `;

  acc[`${label}_up`] = style => css`
    @media screen and (min-width: ${bp[label]}px) {
      ${style};
    }
  `;

  return acc;
}, {}) as MediaQueries;

export const centered = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
`;

export const ellipsis = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// export const transition = ".5s ease";

export const Sticky = styled.div`
  position: sticky;
  top: 0;
`;

/**
 * CSS mixin for hover-revealed elements (overflow buttons, action buttons, etc.)
 * On desktop: Elements appear on hover
 * On touch devices: Elements visible when parent has data-selected="true"
 */
export const showOnHoverCSS = (className: string = "show-on-hover") => css`
  .${className} {
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  @media not (hover: none) {
    &:hover .${className} {
      opacity: 1;
    }
  }
  @media (hover: none) {
    &[data-selected="true"] .${className} {
      opacity: 1;
    }
  }
`;
